import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import {
	ActivityCsvTemplate,
	ContentCsvTemplate,
	CourseCsvTemplate,
	LessonCsvTemplate,
	LocationCsvTemplate,
	SetupWizardPayloadObjectKeys,
	SchoolAdminToLocationMapCsvKeys,
	SchoolAdminToLocationMapCsvTemplate,
	SectionCsvTemplate,
	SETUP_WIZARD_PAYLOAD_UNIQUE_ID_FIELDS_MAP,
	SETUP_WIZARD_PAYLOAD_UPSERT_ONLY,
	SetupWizardActivityFormPayload,
	SetupWizardClientFormPayload,
	SetupWizardContentFormPayload,
	SetupWizardCourseFormPayload,
	SetupWizardLessonFormPayload,
	SetupWizardLocationFormPayload,
	SetupWizardPayload,
	SetupWizardSteps,
	SetupWizardUnitFormPayload,
	SetupWizardUserFormPayload,
	UnitCsvTemplate,
	UserCsvTemplate,
	UserToSectionCsvKeys,
	UserToSectionMapCsvTemplate,
	BeforeAfter,
	SetupWizardPayloadObjectKey,
	SetupWizardUser,
	SetupWizardSection,
	SetupWizardUserToSection,
	SectionCsvKeys,
	UserCsvKeys,
	TextUtils,
	SetupWizardLocation,
	LocationCsvKeys,
	SetupWizardSchoolAdminToLocation,
} from '@mira-avant/mtlibs';
import { ClientService } from './client.service';

const EMPTY_PREVIEW: SetupWizardPayload = {
	stats: {
		usersUpserts: 0,
		usersToSectionsUpserts: 0,
		usersToSectionsDeletes: 0,
		locationsCreates: 0,
		locationsUpdates: 0,
		schoolAdminsToLocationsUpserts: 0,
		schoolAdminsToLocationsDeletes: 0,
		sectionsCreates: 0,
		sectionsUpdates: 0,
		coursesCreates: 0,
		coursesUpdates: 0,
		unitsCreates: 0,
		unitsUpdates: 0,
		lessonsCreates: 0,
		lessonsUpdates: 0,
		activitiesCreates: 0,
		activitiesUpdates: 0,
		contentsCreates: 0,
		contentsUpdates: 0,
	},
	payload: {
		users: [],
		usersToSections: [],
		locations: [],
		schoolAdminsToLocations: [],
		sections: [],
		courses: [],
		units: [],
		lessons: [],
		activities: [],
		contents: [],
	},
} as const;

@Injectable({
	providedIn: 'root',
})
export class SetupWizard {
	private readonly BASE_PATH = 'setup-wizard';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public previewLoadingProgress$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
	public preview$: BehaviorSubject<SetupWizardPayload> = new BehaviorSubject<SetupWizardPayload>(EMPTY_PREVIEW);
	private readonly userCognitoDetailsCache = new Map<string, Promise<UserCsvTemplate | undefined>>();

	constructor(
		private api: ApiService,
		private clientService: ClientService
	) {}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async loadClientPayload$(steps: SetupWizardSteps[], clientUuid?: string): Promise<SetupWizardPayload> {
		if (!clientUuid) {
			this.previewLoadingProgress$.next(0);

			this.preview$.next(EMPTY_PREVIEW);

			return EMPTY_PREVIEW;
		}

		return this.withLoading(async () => {
			this.previewLoadingProgress$.next(0);

			const payload: SetupWizardPayload = { ...EMPTY_PREVIEW };

			const promises = steps.map(step => {
				switch (step) {
					case SetupWizardSteps.USERS:
						const userPromises = [];

						const usersPromise = this.getUsers$(clientUuid).then((res: UserCsvTemplate[]) => {
							payload.payload.users = res.map(user => ({
								before: user,
								after: undefined,
							}));
						});
						userPromises.push(usersPromise);

						const usersToSectionsPromise = this.getUsersToSections$(clientUuid).then(
							(res: UserToSectionMapCsvTemplate[]) => {
								payload.payload.usersToSections = res.map(userSection => ({
									before: userSection,
									after: undefined,
								}));
							}
						);
						userPromises.push(usersToSectionsPromise);

						return Promise.all(userPromises).then(() => {
							//Update progress for each step completed the value should always be between 0 and 100
							this.previewLoadingProgress$.next(
								this.previewLoadingProgress$.value + Math.floor(100 / steps.length)
							);
						});
					case SetupWizardSteps.LOCATIONS:
						const locationPromises = [];

						const locationsPromise = this.getLocations$(clientUuid).then((res: LocationCsvTemplate[]) => {
							payload.payload.locations = res.map(location => ({
								before: location,
								after: undefined,
							}));
						});

						locationPromises.push(locationsPromise);

						const schoolAdminsToLocationsPromise = this.getSchoolAdminsToLocations$(clientUuid).then(
							(res: SchoolAdminToLocationMapCsvTemplate[]) => {
								payload.payload.schoolAdminsToLocations = res.map(userLocation => ({
									before: userLocation,
									after: undefined,
								}));
							}
						);

						locationPromises.push(schoolAdminsToLocationsPromise);

						return Promise.all(locationPromises).then(() => {
							//Update progress for each step completed the value should always be between 0 and 100
							this.previewLoadingProgress$.next(
								this.previewLoadingProgress$.value + Math.floor(100 / steps.length)
							);
						});
					case SetupWizardSteps.SECTIONS:
						return this.getSections$(clientUuid).then((res: SectionCsvTemplate[]) => {
							//Update progress for each step completed the value should always be between 0 and 100
							this.previewLoadingProgress$.next(
								this.previewLoadingProgress$.value + Math.floor(100 / steps.length)
							);
							payload.payload.sections = res.map(section => ({
								before: section,
								after: undefined,
							}));
						});
					case SetupWizardSteps.COURSE_WORK:
						const courseWorkPromises = [];

						const coursesPromise = this.getCourses$(clientUuid).then((res: CourseCsvTemplate[]) => {
							payload.payload.courses = res.map(course => ({
								before: course,
								after: undefined,
							}));
						});

						courseWorkPromises.push(coursesPromise);

						const unitsPromise = this.getUnits$(clientUuid).then((res: UnitCsvTemplate[]) => {
							payload.payload.units = res.map(unit => ({
								before: unit,
								after: undefined,
							}));
						});

						courseWorkPromises.push(unitsPromise);

						const lessonsPromise = this.getLessons$(clientUuid).then((res: LessonCsvTemplate[]) => {
							payload.payload.lessons = res.map(lesson => ({
								before: lesson,
								after: undefined,
							}));
						});

						courseWorkPromises.push(lessonsPromise);

						const activitiesPromise = this.getActivities$(clientUuid).then((res: ActivityCsvTemplate[]) => {
							payload.payload.activities = res.map(activity => ({
								before: activity,
								after: undefined,
							}));
						});

						courseWorkPromises.push(activitiesPromise);

						const contentsPromise = this.getContents$(clientUuid).then((res: ContentCsvTemplate[]) => {
							payload.payload.contents = res.map(content => ({
								before: content,
								after: undefined,
							}));
						});

						courseWorkPromises.push(contentsPromise);

						return Promise.all(courseWorkPromises).then(() => {
							//Update progress for each step completed the value should always be between 0 and 100
							this.previewLoadingProgress$.next(
								this.previewLoadingProgress$.value + Math.floor(100 / steps.length)
							);
						});
					default:
						return Promise.resolve(undefined);
				}
			});

			await Promise.all(promises);

			this.preview$.next(payload);
			this.previewLoadingProgress$.next(100);

			console.log('TODO: Remove me: Setup Wizard Payload:', payload);

			return payload;
		});
	}

	public mergeToPreview(payload: SetupWizardPayload) {
		const currentPreview = this.preview$.getValue();
		this.preview$.next(this.mergePayloads(currentPreview!, payload));
	}

	public async getUsers$(clientUuid: string): Promise<UserCsvTemplate[]> {
		return this.withLoading(
			async () =>
				await firstValueFrom(this.api.post<UserCsvTemplate[]>(this.BASE_PATH + '/users/' + clientUuid, {}))
		);
	}

	public async getUserCognitoDetails$(clientUuid: string, userUuid: string): Promise<UserCsvTemplate | undefined> {
		const key = `${clientUuid}:${userUuid}`;

		// First call for this pair → create and cache the promise
		if (!this.userCognitoDetailsCache.has(key)) {
			const fetchPromise = this.withLoading(async () => {
				try {
					return await firstValueFrom(
						this.api.post<UserCsvTemplate>(
							this.BASE_PATH + `/user-cognito-details/${clientUuid}/${userUuid}`,
							{}
						)
					);
				} catch (error) {
					// Any error (HTTP 4xx/5xx, network, etc.) → cache and return undefined
					return undefined;
				}
			});

			this.userCognitoDetailsCache.set(key, fetchPromise);
		}

		// Subsequent calls (or concurrent calls) just await the same promise
		return this.userCognitoDetailsCache.get(key)!;
	}

	public async getUsersToSections$(clientUuid: string): Promise<UserToSectionMapCsvTemplate[]> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<UserToSectionMapCsvTemplate[]>(
						this.BASE_PATH + '/users-to-sections/' + clientUuid,
						{}
					)
				)
		);
	}

	public async getLocations$(clientUuid: string): Promise<LocationCsvTemplate[]> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<LocationCsvTemplate[]>(this.BASE_PATH + '/locations/' + clientUuid, {})
				)
		);
	}

	public async getSchoolAdminsToLocations$(clientUuid: string): Promise<SchoolAdminToLocationMapCsvTemplate[]> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<SchoolAdminToLocationMapCsvTemplate[]>(
						this.BASE_PATH + '/school-admins-to-locations/' + clientUuid,
						{}
					)
				)
		);
	}

	public async getSections$(clientUuid: string): Promise<SectionCsvTemplate[]> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<SectionCsvTemplate[]>(this.BASE_PATH + '/sections/' + clientUuid, {})
				)
		);
	}

	public async getCourses$(clientUuid: string): Promise<CourseCsvTemplate[]> {
		return this.withLoading(
			async () =>
				await firstValueFrom(this.api.post<CourseCsvTemplate[]>(this.BASE_PATH + '/courses/' + clientUuid, {}))
		);
	}

	public async getUnits$(clientUuid: string): Promise<UnitCsvTemplate[]> {
		return this.withLoading(
			async () =>
				await firstValueFrom(this.api.post<UnitCsvTemplate[]>(this.BASE_PATH + '/units/' + clientUuid, {}))
		);
	}

	public async getLessons$(clientUuid: string): Promise<LessonCsvTemplate[]> {
		return this.withLoading(
			async () =>
				await firstValueFrom(this.api.post<LessonCsvTemplate[]>(this.BASE_PATH + '/lessons/' + clientUuid, {}))
		);
	}

	public async getActivities$(clientUuid: string): Promise<ActivityCsvTemplate[]> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<ActivityCsvTemplate[]>(this.BASE_PATH + '/activities/' + clientUuid, {})
				)
		);
	}

	public async getContents$(clientUuid: string): Promise<ContentCsvTemplate[]> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<ContentCsvTemplate[]>(this.BASE_PATH + '/contents/' + clientUuid, {})
				)
		);
	}

	public async updateClient$(clientUuid: string, payload: SetupWizardClientFormPayload): Promise<boolean> {
		return this.withLoading(async () => {
			const result = await firstValueFrom(
				this.api.post<boolean>(this.BASE_PATH + '/update-client/' + clientUuid, payload)
			);

			if (clientUuid) {
				await this.clientService.updateFromSetupWizard$(clientUuid);
			}

			return result;
		});
	}

	public async upsertUser$(clientUuid: string, payload: SetupWizardUserFormPayload): Promise<UserCsvTemplate> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<UserCsvTemplate>(this.BASE_PATH + '/upsert-user/' + clientUuid, payload)
				)
		);
	}

	public async upsertLocation$(
		clientUuid: string,
		payload: SetupWizardLocationFormPayload
	): Promise<LocationCsvTemplate> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<LocationCsvTemplate>(this.BASE_PATH + '/upsert-location/' + clientUuid, payload)
				)
		);
	}

	public async upsertCourse$(clientUuid: string, payload: SetupWizardCourseFormPayload): Promise<CourseCsvTemplate> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<CourseCsvTemplate>(this.BASE_PATH + '/upsert-course/' + clientUuid, payload)
				)
		);
	}

	public async upsertUnit$(clientUuid: string, payload: SetupWizardUnitFormPayload): Promise<UnitCsvTemplate> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<UnitCsvTemplate>(this.BASE_PATH + '/upsert-unit/' + clientUuid, payload)
				)
		);
	}

	public async upsertLesson$(clientUuid: string, payload: SetupWizardLessonFormPayload): Promise<LessonCsvTemplate> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<LessonCsvTemplate>(this.BASE_PATH + '/upsert-lesson/' + clientUuid, payload)
				)
		);
	}

	public async upsertActivity$(
		clientUuid: string,
		payload: SetupWizardActivityFormPayload
	): Promise<ActivityCsvTemplate> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<ActivityCsvTemplate>(this.BASE_PATH + '/upsert-activity/' + clientUuid, payload)
				)
		);
	}

	public async upsertContent$(
		clientUuid: string,
		payload: SetupWizardContentFormPayload
	): Promise<ContentCsvTemplate> {
		return this.withLoading(
			async () =>
				await firstValueFrom(
					this.api.post<ContentCsvTemplate>(this.BASE_PATH + '/upsert-content/' + clientUuid, payload)
				)
		);
	}

	//A remove operation removes the entry entirely from the payload - before and after alike the row
	//will be deleted. This is only used to remove a pending 'new' object where the after property is
	//set and there's no before property.
	public removePayloadItem<T>(objectKey: SetupWizardPayloadObjectKey, after: BeforeAfter<T>['after']) {
		const currentPreview = this.preview$.getValue();
		const currentArray = currentPreview.payload[objectKey] as BeforeAfter<T>[];

		const index = this.findIndexByAfter(objectKey, after, currentArray);

		if (index === -1) {
			return;
		}

		const item = currentArray[index];

		// Only remove truly new items (no before)
		if (item.before !== undefined) {
			return;
		}

		const updatedArray = currentArray.filter((_, i) => i !== index);

		this.preview$.next({
			...currentPreview,
			payload: {
				...currentPreview.payload,
				[objectKey]: updatedArray,
			},
		});
	}

	//A reset operation occurs when there is both a before and after property on the item and
	//they are not the same. In this case the after property is simply deleted leaving only the
	//before property.
	public resetPayloadItem<T>(objectKey: SetupWizardPayloadObjectKey, after: BeforeAfter<T>['after']) {
		const currentPreview = this.preview$.getValue();
		const currentArray = currentPreview.payload[objectKey] as BeforeAfter<T>[];

		const index = this.findIndexByAfter(objectKey, after, currentArray);

		if (index === -1) {
			return;
		}

		const item = currentArray[index];

		if (item.before === undefined || item.after === undefined) {
			return;
		}

		const updatedArray = [...currentArray];
		updatedArray[index] = { ...item, after: undefined };

		this.preview$.next({
			...currentPreview,
			payload: {
				...currentPreview.payload,
				[objectKey]: updatedArray,
			},
		});
	}

	private findIndexByAfter<T>(
		objectKey: SetupWizardPayloadObjectKey,
		after: BeforeAfter<T>['after'],
		array: BeforeAfter<T>[]
	): number {
		const afterAsRecord = after as Record<string, unknown>;

		if (objectKey === SetupWizardPayloadObjectKeys.usersToSections) {
			return array.findIndex(item => {
				const existing = (item.before ?? item.after) as Record<string, unknown>;
				if (!existing) {
					return false;
				}

				const userMatch =
					(TextUtils.isNonEmptyString(existing[UserToSectionCsvKeys.userUuid]) &&
						existing[UserToSectionCsvKeys.userUuid] === afterAsRecord[UserToSectionCsvKeys.userUuid]) ||
					(TextUtils.isNonEmptyString(existing[UserToSectionCsvKeys.schoolIdOrEmail]) &&
						existing[UserToSectionCsvKeys.schoolIdOrEmail] ===
							afterAsRecord[UserToSectionCsvKeys.schoolIdOrEmail]);

				const sectionMatch =
					(TextUtils.isNonEmptyString(existing[UserToSectionCsvKeys.sectionUuid]) &&
						existing[UserToSectionCsvKeys.sectionUuid] ===
							afterAsRecord[UserToSectionCsvKeys.sectionUuid]) ||
					(TextUtils.isNonEmptyString(existing[UserToSectionCsvKeys.ssoSectionId]) &&
						existing[UserToSectionCsvKeys.ssoSectionId] ===
							afterAsRecord[UserToSectionCsvKeys.ssoSectionId]);

				return userMatch && sectionMatch;
			});
		}

		if (objectKey === SetupWizardPayloadObjectKeys.schoolAdminsToLocations) {
			return array.findIndex(item => {
				const existing = (item.before ?? item.after) as Record<string, unknown>;

				if (!existing) {
					return false;
				}

				const userMatch =
					(TextUtils.isNonEmptyString(existing[SchoolAdminToLocationMapCsvKeys.userUuid]) &&
						existing[SchoolAdminToLocationMapCsvKeys.userUuid] ===
							afterAsRecord[SchoolAdminToLocationMapCsvKeys.userUuid]) ||
					(TextUtils.isNonEmptyString(existing[SchoolAdminToLocationMapCsvKeys.schoolIdOrEmail]) &&
						existing[SchoolAdminToLocationMapCsvKeys.schoolIdOrEmail] ===
							afterAsRecord[SchoolAdminToLocationMapCsvKeys.schoolIdOrEmail]);

				const locationMatch =
					(TextUtils.isNonEmptyString(existing[SchoolAdminToLocationMapCsvKeys.locationUuid]) &&
						existing[SchoolAdminToLocationMapCsvKeys.locationUuid] ===
							afterAsRecord[SchoolAdminToLocationMapCsvKeys.locationUuid]) ||
					(TextUtils.isNonEmptyString(existing[SchoolAdminToLocationMapCsvKeys.ssoLocationId]) &&
						existing[SchoolAdminToLocationMapCsvKeys.ssoLocationId] ===
							afterAsRecord[SchoolAdminToLocationMapCsvKeys.ssoLocationId]);

				return userMatch && locationMatch;
			});
		}

		// Generic case using unique ID fields map
		const idKeys = SETUP_WIZARD_PAYLOAD_UNIQUE_ID_FIELDS_MAP[objectKey];

		if (!idKeys?.length) {
			console.warn(`No unique ID fields for ${objectKey}`);
			return -1;
		}

		return array.findIndex(item => {
			const itemAsRecord = (item.before ?? item.after) as Record<string, unknown>;

			for (const key of idKeys) {
				const val = afterAsRecord[key];

				if (val == null) {
					continue;
				}

				if (itemAsRecord[key] === val) {
					return true;
				}
			}

			return false;
		});
	}

	public updateExistingPayloadItems<T>(objectKey: SetupWizardPayloadObjectKey, befores: BeforeAfter<T>['before'][]) {
		const currentPreview = this.preview$.getValue();
		const currentArray = currentPreview.payload[objectKey] as BeforeAfter<T>[];
		const updatedArray = [...currentArray];

		let changed = false;

		befores.forEach(before => {
			const beforeAsRecord = before as Record<string, unknown>;

			const index = updatedArray.findIndex(item => {
				const existing = (item.before ?? item.after) as Record<string, unknown>;

				if (!existing) {
					return false;
				}

				const idKeys = SETUP_WIZARD_PAYLOAD_UNIQUE_ID_FIELDS_MAP[objectKey];

				if (!idKeys?.length) {
					return false;
				}

				return idKeys.some(key => existing[key] === beforeAsRecord[key]);
			});

			if (index === -1) {
				return;
			}

			// Only update if different (optional deep equality check can be added if needed)
			if (JSON.stringify(updatedArray[index].before) !== JSON.stringify(before)) {
				updatedArray[index] = { ...updatedArray[index], before };
				changed = true;
			}
		});

		if (!changed) {
			console.warn(`No changes detected for ${objectKey}`);
			return;
		}

		this.preview$.next({
			...currentPreview,
			payload: {
				...currentPreview.payload,
				[objectKey]: updatedArray,
			},
		});
	}

	public updateExistingPayloadItem<T>(objectKey: SetupWizardPayloadObjectKey, before: BeforeAfter<T>['before']) {
		this.updateExistingPayloadItems(objectKey, [before]);
	}

	public upsertPayloadItem<T>(objectKey: SetupWizardPayloadObjectKey, after: BeforeAfter<T>['after']) {
		const currentPreview = this.preview$.getValue();
		const currentArray = currentPreview.payload[objectKey] as BeforeAfter<T>[];

		const { updatedArray, isCreate, somethingChanged } = this.computeUpdatedItem(objectKey, after, currentArray);

		if (!somethingChanged) {
			console.warn(`No real change detected for ${objectKey}`, after);
			return;
		}

		const stats = { ...currentPreview.stats };

		if (SETUP_WIZARD_PAYLOAD_UPSERT_ONLY.has(objectKey)) {
			let statKey: keyof SetupWizardPayload['stats'];
			switch (objectKey) {
				case SetupWizardPayloadObjectKeys.users:
					statKey = `${SetupWizardPayloadObjectKeys.users}Upserts`;
					break;
				case SetupWizardPayloadObjectKeys.usersToSections:
					statKey = `${SetupWizardPayloadObjectKeys.usersToSections}Upserts`;
					break;
				case SetupWizardPayloadObjectKeys.schoolAdminsToLocations:
					statKey = `${SetupWizardPayloadObjectKeys.schoolAdminsToLocations}Upserts`;
					break;
				default:
					console.warn(`No stat key found for ${objectKey}`);
					return;
			}
			stats[statKey] = (stats[statKey] ?? 0) + 1;
		} else {
			const createKey = `${objectKey}Creates` as keyof SetupWizardPayload['stats'];
			const updateKey = `${objectKey}Updates` as keyof SetupWizardPayload['stats'];

			if (isCreate && createKey in stats) {
				stats[createKey] = (stats[createKey] ?? 0) + 1;
			} else if (!isCreate && updateKey in stats) {
				stats[updateKey] = (stats[updateKey] ?? 0) + 1;
			}
		}

		console.log('New payload item for', {
			isCreate,
			somethingChanged,
			objectKey,
			after,
			updatedArray,
			currentPreviewPayload: {
				...currentPreview.payload,
				[objectKey]: updatedArray,
			},
		});

		this.preview$.next({
			...currentPreview,
			stats,
			payload: {
				...currentPreview.payload,
				[objectKey]: updatedArray,
			},
		});
	}

	public mergePayloads(primary: SetupWizardPayload, partial: SetupWizardPayload): SetupWizardPayload {
		let result = structuredClone(primary);

		(Object.keys(partial.payload) as (keyof SetupWizardPayload['payload'])[]).forEach(key => {
			const rows = partial.payload[key];
			if (rows && rows.length > 0) {
				result = this.mergePayloadsForKey(result, rows, key);
			}
		});

		return result;
	}

	private mergePayloadsForKey<K extends keyof SetupWizardPayload['payload']>(
		primary: SetupWizardPayload,
		partialRows: SetupWizardPayload['payload'][K],
		objectKey: K
	): SetupWizardPayload {
		const result = structuredClone(primary);

		if (!partialRows || partialRows.length === 0) {
			return result;
		}

		// Now TypeScript knows exactly what the array element type is
		type Row = SetupWizardPayload['payload'][K][number];
		let array: BeforeAfter<Row>[] = result.payload[objectKey] as BeforeAfter<Row>[];

		let creates = 0;
		let updates = 0;

		partialRows.forEach(row => {
			// row is already the correct Row type — no assertion needed
			const { updatedArray, isCreate, somethingChanged } = this.computeUpdatedItem(objectKey, row, array);

			array = updatedArray;

			if (!somethingChanged) {
				return;
			}

			if (isCreate) {
				creates += 1;
			} else {
				updates += 1;
			}
		});

		// Write-back is now type-safe — no assertion needed
		(result.payload as Record<string, BeforeAfter<unknown>[]>)[objectKey] = array;

		const totalChanges = creates + updates;
		if (totalChanges > 0) {
			if (SETUP_WIZARD_PAYLOAD_UPSERT_ONLY.has(objectKey)) {
				let statKey: keyof SetupWizardPayload['stats'];

				switch (objectKey) {
					case SetupWizardPayloadObjectKeys.users:
						statKey = `${SetupWizardPayloadObjectKeys.users}Upserts`;
						break;
					case SetupWizardPayloadObjectKeys.usersToSections:
						statKey = `${SetupWizardPayloadObjectKeys.usersToSections}Upserts`;
						break;
					case SetupWizardPayloadObjectKeys.schoolAdminsToLocations:
						statKey = `${SetupWizardPayloadObjectKeys.schoolAdminsToLocations}Upserts`;
						break;
					default:
						return result;
				}

				result.stats[statKey] = (result.stats[statKey] ?? 0) + totalChanges;
			} else {
				const createKey = `${objectKey}Creates` as keyof SetupWizardPayload['stats'];
				const updateKey = `${objectKey}Updates` as keyof SetupWizardPayload['stats'];

				if (createKey in result.stats) {
					result.stats[createKey] = (result.stats[createKey] ?? 0) + creates;
				}

				if (updateKey in result.stats) {
					result.stats[updateKey] = (result.stats[updateKey] ?? 0) + updates;
				}
			}
		}

		return result;
	}

	private computeUpdatedItem<T>(
		objectKey: keyof SetupWizardPayload['payload'],
		after: BeforeAfter<T>['after'],
		currentArray: BeforeAfter<T>[]
	): {
		updatedArray: BeforeAfter<T>[];
		isCreate: boolean;
		somethingChanged: boolean;
	} {
		let existingIndex: number = -1;
		const afterAsRecord = after as Record<string, unknown>;

		if (objectKey === SetupWizardPayloadObjectKeys.usersToSections) {
			existingIndex = currentArray.findIndex(item => {
				const existing = (item.before ?? item.after) as Record<string, unknown>;

				if (!existing) {
					console.warn(`No existing item found for ${objectKey} with after data:`, afterAsRecord);
					return false;
				}

				const userMatch: boolean =
					(TextUtils.isNonEmptyString(existing[UserToSectionCsvKeys.userUuid]) &&
						TextUtils.isNonEmptyString(existing[UserToSectionCsvKeys.userUuid]) &&
						TextUtils.isNonEmptyString(afterAsRecord[UserToSectionCsvKeys.userUuid]) &&
						existing[UserToSectionCsvKeys.userUuid] === afterAsRecord[UserToSectionCsvKeys.userUuid]) ||
					(TextUtils.isNonEmptyString(existing[UserToSectionCsvKeys.schoolIdOrEmail]) &&
						TextUtils.isNonEmptyString(afterAsRecord[UserToSectionCsvKeys.schoolIdOrEmail]) &&
						existing[UserToSectionCsvKeys.schoolIdOrEmail] ===
							afterAsRecord[UserToSectionCsvKeys.schoolIdOrEmail]);

				const sectionMatch: boolean =
					(TextUtils.isNonEmptyString(existing[UserToSectionCsvKeys.sectionUuid]) &&
						TextUtils.isNonEmptyString(afterAsRecord[UserToSectionCsvKeys.sectionUuid]) &&
						existing[UserToSectionCsvKeys.sectionUuid] ===
							afterAsRecord[UserToSectionCsvKeys.sectionUuid]) ||
					(TextUtils.isNonEmptyString(existing[UserToSectionCsvKeys.ssoSectionId]) &&
						TextUtils.isNonEmptyString(afterAsRecord[UserToSectionCsvKeys.ssoSectionId]) &&
						existing[UserToSectionCsvKeys.ssoSectionId] ===
							afterAsRecord[UserToSectionCsvKeys.ssoSectionId]);

				return userMatch && sectionMatch;
			});
		} else if (objectKey === SetupWizardPayloadObjectKeys.schoolAdminsToLocations) {
			existingIndex = currentArray.findIndex(item => {
				const existing = (item.before ?? item.after) as Record<string, unknown>;

				if (!existing) {
					console.warn(`No existing item found for ${objectKey} with after data:`, afterAsRecord);
					return false;
				}

				const userMatch: boolean =
					(TextUtils.isNonEmptyString(existing[SchoolAdminToLocationMapCsvKeys.userUuid]) &&
						TextUtils.isNonEmptyString(afterAsRecord[SchoolAdminToLocationMapCsvKeys.userUuid]) &&
						existing[SchoolAdminToLocationMapCsvKeys.userUuid] ===
							afterAsRecord[SchoolAdminToLocationMapCsvKeys.userUuid]) ||
					(TextUtils.isNonEmptyString(existing[SchoolAdminToLocationMapCsvKeys.schoolIdOrEmail]) &&
						TextUtils.isNonEmptyString(afterAsRecord[SchoolAdminToLocationMapCsvKeys.schoolIdOrEmail]) &&
						existing[SchoolAdminToLocationMapCsvKeys.schoolIdOrEmail] ===
							afterAsRecord[SchoolAdminToLocationMapCsvKeys.schoolIdOrEmail]);

				const locationMatch: boolean =
					(TextUtils.isNonEmptyString(existing[SchoolAdminToLocationMapCsvKeys.locationUuid]) &&
						TextUtils.isNonEmptyString(afterAsRecord[SchoolAdminToLocationMapCsvKeys.locationUuid]) &&
						existing[SchoolAdminToLocationMapCsvKeys.locationUuid] ===
							afterAsRecord[SchoolAdminToLocationMapCsvKeys.locationUuid]) ||
					(TextUtils.isNonEmptyString(existing[SchoolAdminToLocationMapCsvKeys.ssoLocationId]) &&
						TextUtils.isNonEmptyString(afterAsRecord[SchoolAdminToLocationMapCsvKeys.ssoLocationId]) &&
						existing[SchoolAdminToLocationMapCsvKeys.ssoLocationId] ===
							afterAsRecord[SchoolAdminToLocationMapCsvKeys.ssoLocationId]);

				return userMatch && locationMatch;
			});
		} else {
			const idKeyNames = SETUP_WIZARD_PAYLOAD_UNIQUE_ID_FIELDS_MAP[objectKey];

			if (!idKeyNames || idKeyNames.length === 0) {
				console.warn(`No unique ID fields defined for ${objectKey}`, SETUP_WIZARD_PAYLOAD_UNIQUE_ID_FIELDS_MAP);

				return {
					updatedArray: currentArray,
					isCreate: false,
					somethingChanged: false,
				};
			}

			existingIndex = currentArray.findIndex(item => {
				for (const keyName of idKeyNames) {
					if (typeof keyName !== 'string') {
						continue;
					}

					const val = (after as Record<string, unknown>)[keyName];

					if (val === undefined || val === null || !val) {
						continue;
					}

					const afterVal = item.after?.[keyName as keyof T];
					const beforeVal = item.before?.[keyName as keyof T];

					if (afterVal === val || beforeVal === val) {
						return true;
					}
				}

				return false;
			});
		}

		const isCreate: boolean = existingIndex === -1;
		let updatedArray: BeforeAfter<T>[] = [...currentArray];
		let somethingChanged: boolean = false;

		if (isCreate) {
			updatedArray.push({ before: undefined, after });
			somethingChanged = true;
		} else {
			const existing = updatedArray[existingIndex]!;

			const beforeStr: string = JSON.stringify(existing.before ?? {});
			const afterStr: string = JSON.stringify(after);

			if (beforeStr === afterStr) {
				if (existing.after !== undefined) {
					existing.after = undefined;
					somethingChanged = true;
				}
			} else {
				existing.after = { ...after } as BeforeAfter<T>['after'];
				somethingChanged = true;
			}

			updatedArray[existingIndex] = { ...existing };
		}

		return {
			updatedArray,
			isCreate,
			somethingChanged,
		};
	}

	/**
	 * If the requested user exists in the requested section return the find()
	 * result of the index in the usersToSections array.
	 *
	 * @private
	 * @param {SetupWizardUser} user
	 * @param {SetupWizardSection} section
	 * @returns {number}
	 */
	private isUserInSection(user: SetupWizardUser, section: SetupWizardSection): number {
		// First we need to determine which user and section from the inputs to work with.
		const userBeforeAfter = user.after ?? user.before;
		const sectionBeforeAfter = section.after ?? section.before;

		if (!userBeforeAfter || !sectionBeforeAfter) {
			throw new Error('User or section data is missing');
		}

		return this.preview$.value.payload[SetupWizardPayloadObjectKeys.usersToSections]?.findIndex(
			(link: SetupWizardUserToSection) => {
				const linkBeforeAfter = link.after ?? link.before;

				if (!linkBeforeAfter) {
					throw new Error('Link data is missing');
				}

				//We need to determine if this is the section we are looking for by comparing one of the two
				//supported unique IDs
				const sectionMatch =
					(TextUtils.isNonEmptyString(sectionBeforeAfter[SectionCsvKeys.ssoSectionId]) &&
						TextUtils.isNonEmptyString(linkBeforeAfter[UserToSectionCsvKeys.ssoSectionId]) &&
						sectionBeforeAfter[SectionCsvKeys.ssoSectionId] ===
							linkBeforeAfter[UserToSectionCsvKeys.ssoSectionId]) ||
					(TextUtils.isNonEmptyString(sectionBeforeAfter[SectionCsvKeys.sectionUuid]) &&
						TextUtils.isNonEmptyString(linkBeforeAfter[UserToSectionCsvKeys.sectionUuid]) &&
						sectionBeforeAfter[SectionCsvKeys.sectionUuid] ===
							linkBeforeAfter[UserToSectionCsvKeys.sectionUuid]);

				const userMatch =
					(TextUtils.isNonEmptyString(userBeforeAfter[UserCsvKeys.schoolIdOrEmail]) &&
						TextUtils.isNonEmptyString(linkBeforeAfter[UserToSectionCsvKeys.schoolIdOrEmail]) &&
						userBeforeAfter[UserCsvKeys.schoolIdOrEmail] ===
							linkBeforeAfter[UserToSectionCsvKeys.schoolIdOrEmail]) ||
					(TextUtils.isNonEmptyString(userBeforeAfter[UserCsvKeys.userUuid]) &&
						TextUtils.isNonEmptyString(linkBeforeAfter[UserToSectionCsvKeys.userUuid]) &&
						userBeforeAfter[UserCsvKeys.userUuid] === linkBeforeAfter[UserToSectionCsvKeys.userUuid]);

				return sectionMatch && userMatch;
			}
		);
	}

	/**
	 * If the requested user exists in the requested location return the find()
	 * result of the index in the schoolAdminsToLocations array.
	 *
	 * @private
	 * @param {SetupWizardUser} user
	 * @param {SetupWizardLocation} location
	 * @returns {number}
	 */
	private isUserInLocation(user: SetupWizardUser, location: SetupWizardLocation): number {
		// First we need to determine which user and location from the inputs to work with.
		const userBeforeAfter = user.after ?? user.before;
		const locationBeforeAfter = location.after ?? location.before;

		if (!userBeforeAfter || !locationBeforeAfter) {
			throw new Error('User or location data is missing');
		}

		return this.preview$.value.payload[SetupWizardPayloadObjectKeys.schoolAdminsToLocations]?.findIndex(
			(link: SetupWizardSchoolAdminToLocation) => {
				const linkBeforeAfter = link.after ?? link.before;

				if (!linkBeforeAfter) {
					throw new Error('Link data is missing');
				}

				//We need to determine if this is the location we are looking for by comparing one of the two
				//supported unique IDs
				const locationMatch =
					(TextUtils.isNonEmptyString(locationBeforeAfter[LocationCsvKeys.ssoLocationId]) &&
						TextUtils.isNonEmptyString(linkBeforeAfter[SchoolAdminToLocationMapCsvKeys.ssoLocationId]) &&
						locationBeforeAfter[LocationCsvKeys.ssoLocationId] ===
							linkBeforeAfter[SchoolAdminToLocationMapCsvKeys.ssoLocationId]) ||
					(TextUtils.isNonEmptyString(locationBeforeAfter[LocationCsvKeys.locationUuid]) &&
						TextUtils.isNonEmptyString(linkBeforeAfter[SchoolAdminToLocationMapCsvKeys.locationUuid]) &&
						locationBeforeAfter[LocationCsvKeys.locationUuid] ===
							linkBeforeAfter[SchoolAdminToLocationMapCsvKeys.locationUuid]);

				const userMatch =
					(TextUtils.isNonEmptyString(userBeforeAfter[UserCsvKeys.schoolIdOrEmail]) &&
						TextUtils.isNonEmptyString(linkBeforeAfter[SchoolAdminToLocationMapCsvKeys.schoolIdOrEmail]) &&
						userBeforeAfter[UserCsvKeys.schoolIdOrEmail] ===
							linkBeforeAfter[SchoolAdminToLocationMapCsvKeys.schoolIdOrEmail]) ||
					(TextUtils.isNonEmptyString(userBeforeAfter[UserCsvKeys.userUuid]) &&
						TextUtils.isNonEmptyString(linkBeforeAfter[SchoolAdminToLocationMapCsvKeys.userUuid]) &&
						userBeforeAfter[UserCsvKeys.userUuid] ===
							linkBeforeAfter[SchoolAdminToLocationMapCsvKeys.userUuid]);

				return locationMatch && userMatch;
			}
		);
	}

	// To 'enroll' a user in a section we first see if the user already exists
	// in the usersToSections list. If they do set the row's enrolled boolean
	// to true. If they don't exist in the array add a new row representing
	// the link then call updatePayloadItem to persist the change.
	public async enroll(user: SetupWizardUser, section: SetupWizardSection) {
		// First we need to determine which user and section from the inputs to work with.
		const userBeforeAfter = user.after ?? user.before;
		const sectionBeforeAfter = section.after ?? section.before;

		if (!userBeforeAfter || !sectionBeforeAfter) {
			throw new Error('User or section data is missing');
		}

		const linkIndex = this.isUserInSection(user, section);

		if (linkIndex === -1) {
			// If the link does not exist, create a new link and add it to the usersToSections array
			const newLink: UserToSectionMapCsvTemplate = {
				[UserToSectionCsvKeys.schoolIdOrEmail]: userBeforeAfter[UserCsvKeys.schoolIdOrEmail],
				[UserToSectionCsvKeys.userUuid]: userBeforeAfter[UserCsvKeys.userUuid],
				[UserToSectionCsvKeys.ssoSectionId]: sectionBeforeAfter[SectionCsvKeys.ssoSectionId],
				[UserToSectionCsvKeys.sectionUuid]: sectionBeforeAfter[SectionCsvKeys.sectionUuid],
				active: true,
				enrolled: true,
			};
			await this.upsertPayloadItem(SetupWizardPayloadObjectKeys.usersToSections, newLink);
		} else {
			// If the link exists, set the enrolled flag to true
			const link = this.preview$.value.payload[SetupWizardPayloadObjectKeys.usersToSections]?.[linkIndex];
			const linkBeforeAfter = link?.after ?? link?.before;

			if (!linkBeforeAfter) {
				throw new Error('Link data is missing');
			}

			if (link) {
				const updatedLink: UserToSectionMapCsvTemplate = {
					...linkBeforeAfter,
					enrolled: true,
				};

				this.upsertPayloadItem(SetupWizardPayloadObjectKeys.usersToSections, updatedLink);
			}
		}
	}

	// To unenroll a student from a section the link must already exist then we
	// simply locate the link row, set enrolled to false, and call
	// call updatePayloadItem to persist the change.
	// If, however, in the link there is just an after property and not a before
	// then we delete the entire link because it didn't exist in the first place.
	public async unenroll(user: SetupWizardUser, section: SetupWizardSection) {
		// First we need to determine which user and section from the inputs to work with.
		const userBeforeAfter = user.after ?? user.before;
		const sectionBeforeAfter = section.after ?? section.before;

		if (!userBeforeAfter || !sectionBeforeAfter) {
			throw new Error('User or section data is missing');
		}

		const linkIndex = this.isUserInSection(user, section);

		if (linkIndex === -1) {
			throw new Error('User is not enrolled in the section');
		} else {
			// If the link exists, set the enrolled flag to false
			const link = this.preview$.value.payload[SetupWizardPayloadObjectKeys.usersToSections]?.[linkIndex];

			// If there is only an after property in the link delete it entirely
			if (link?.after && !link?.before) {
				this.removePayloadItem(SetupWizardPayloadObjectKeys.usersToSections, link.after);
				return;
			}

			const linkBeforeAfter = link?.after ?? link?.before;

			if (!linkBeforeAfter) {
				throw new Error('Link data is missing');
			}

			if (link) {
				const updatedLink: UserToSectionMapCsvTemplate = {
					...linkBeforeAfter,
					enrolled: false,
				};

				this.upsertPayloadItem(SetupWizardPayloadObjectKeys.usersToSections, updatedLink);
			}
		}
	}

	public hasUserEnrollmentChanged(user: SetupWizardUser): boolean {
		const userBeforeAfter = user.after ?? user.before;

		if (!userBeforeAfter) {
			throw new Error('User data is missing');
		}

		return this.preview$.value.payload.usersToSections.some(link => {
			const beforeAfter = link.after ?? link.before;

			if (!beforeAfter) {
				throw new Error('UserToSection link has no before or after state');
			}

			const doesUserMatch =
				(TextUtils.isNonEmptyString(beforeAfter.user_uuid) &&
					beforeAfter.user_uuid === userBeforeAfter.user_uuid) ||
				(TextUtils.isNonEmptyString(beforeAfter.school_id_or_email) &&
					beforeAfter.school_id_or_email === userBeforeAfter.school_id_or_email);

			if (!doesUserMatch) {
				return false;
			}

			if (!link.before && link.after) {
				// added
				return true;
			} else if (link.before && !link.after) {
				// existing
				return false;
			} else if (link.before && link.after && link.before.enrolled !== link.after.enrolled) {
				// enrollment status changed
				return true;
			}

			return false;
		});
	}

	public hasSectionEnrollmentChanged(section: SetupWizardSection): boolean {
		const sectionBeforeAfter = section.after ?? section.before;

		if (!sectionBeforeAfter) {
			throw new Error('Section data is missing');
		}

		return this.preview$.value.payload.usersToSections.some(link => {
			const beforeAfter = link.after ?? link.before;

			if (!beforeAfter) {
				throw new Error('UserToSection link has no before or after state');
			}

			const doesSectionMatch =
				(TextUtils.isNonEmptyString(beforeAfter.section_uuid) &&
					beforeAfter.section_uuid === sectionBeforeAfter.section_uuid) ||
				(TextUtils.isNonEmptyString(beforeAfter.sso_section_id) &&
					beforeAfter.sso_section_id === sectionBeforeAfter.sso_section_id);

			if (!doesSectionMatch) {
				return false;
			}

			if (!link.before && link.after) {
				// added
				return true;
			} else if (link.before && !link.after) {
				// existing
				return false;
			} else if (link.before && link.after && link.before.enrolled !== link.after.enrolled) {
				// enrollment status changed
				return true;
			}

			return false;
		});
	}

	// To 'assign' a user in a location we first see if the user already exists
	// in the usersToLocations list. If they do set the row's assigned boolean
	// to true. If they don't exist in the array add a new row representing
	// the link then call updatePayloadItem to persist the change.
	public async assign(user: SetupWizardUser, location: SetupWizardLocation) {
		// First we need to determine which user and location from the inputs to work with.
		const userBeforeAfter = user.after ?? user.before;
		const locationBeforeAfter = location.after ?? location.before;

		if (!userBeforeAfter || !locationBeforeAfter) {
			throw new Error('User or location data is missing');
		}

		const linkIndex = this.isUserInLocation(user, location);

		if (linkIndex === -1) {
			// If the link does not exist, create a new link and add it to the usersToLocations array
			const newLink: SchoolAdminToLocationMapCsvTemplate = {
				[SchoolAdminToLocationMapCsvKeys.schoolIdOrEmail]: userBeforeAfter[UserCsvKeys.schoolIdOrEmail],
				[SchoolAdminToLocationMapCsvKeys.userUuid]: userBeforeAfter[UserCsvKeys.userUuid],
				[SchoolAdminToLocationMapCsvKeys.ssoLocationId]: locationBeforeAfter[LocationCsvKeys.ssoLocationId],
				[SchoolAdminToLocationMapCsvKeys.locationUuid]: locationBeforeAfter[LocationCsvKeys.locationUuid],
				active: true,
			};
			await this.upsertPayloadItem(SetupWizardPayloadObjectKeys.schoolAdminsToLocations, newLink);
		} else {
			// If the link exists, set the assigned flag to true
			const link = this.preview$.value.payload[SetupWizardPayloadObjectKeys.schoolAdminsToLocations]?.[linkIndex];
			const linkBeforeAfter = link?.after ?? link?.before;

			if (!linkBeforeAfter) {
				throw new Error('Link data is missing');
			}

			if (link) {
				const updatedLink: SchoolAdminToLocationMapCsvTemplate = {
					...linkBeforeAfter,
					active: true,
				};

				this.upsertPayloadItem(SetupWizardPayloadObjectKeys.schoolAdminsToLocations, updatedLink);
			}
		}
	}

	// To unassign a student from a location the link must already exist then we
	// simply locate the link row, set assigned to false, and call
	// call updatePayloadItem to persist the change.
	// If, however, in the link there is just an after property and not a before
	// then we delete the entire link because it didn't exist in the first place.
	public async unassign(user: SetupWizardUser, location: SetupWizardLocation) {
		// First we need to determine which user and location from the inputs to work with.
		const userBeforeAfter = user.after ?? user.before;
		const locationBeforeAfter = location.after ?? location.before;

		if (!userBeforeAfter || !locationBeforeAfter) {
			throw new Error('User or location data is missing');
		}

		const linkIndex = this.isUserInLocation(user, location);

		if (linkIndex === -1) {
			throw new Error('User is not assigned in the location');
		} else {
			// If the link exists, set the assigned flag to false
			const link = this.preview$.value.payload[SetupWizardPayloadObjectKeys.schoolAdminsToLocations]?.[linkIndex];

			// If there is only an after property in the link delete it entirely
			if (link?.after && !link?.before) {
				this.removePayloadItem(SetupWizardPayloadObjectKeys.schoolAdminsToLocations, link.after);
				return;
			}

			const linkBeforeAfter = link?.after ?? link?.before;

			if (!linkBeforeAfter) {
				throw new Error('Link data is missing');
			}

			if (link) {
				const updatedLink: SchoolAdminToLocationMapCsvTemplate = {
					...linkBeforeAfter,
					active: false,
				};

				this.upsertPayloadItem(SetupWizardPayloadObjectKeys.schoolAdminsToLocations, updatedLink);
			}
		}
	}

	public hasUserAssignmentChanged(user: SetupWizardUser): boolean {
		const userBeforeAfter = user.after ?? user.before;

		if (!userBeforeAfter) {
			throw new Error('User data is missing');
		}

		return this.preview$.value.payload.schoolAdminsToLocations.some(link => {
			const beforeAfter = link.after ?? link.before;

			if (!beforeAfter) {
				throw new Error('SchoolAdminToLocation link has no before or after state');
			}

			const doesUserMatch =
				(TextUtils.isNonEmptyString(beforeAfter.user_uuid) &&
					beforeAfter.user_uuid === userBeforeAfter.user_uuid) ||
				(TextUtils.isNonEmptyString(beforeAfter.school_id_or_email) &&
					beforeAfter.school_id_or_email === userBeforeAfter.school_id_or_email);

			if (!doesUserMatch) {
				return false;
			}

			if (!link.before && link.after) {
				// added
				return true;
			} else if (link.before && !link.after) {
				// existing
				return false;
			} else if (link.before && link.after && link.before.active !== link.after.active) {
				// assignment status changed
				return true;
			}

			return false;
		});
	}

	public hasLocationAssignmentChanged(location: SetupWizardLocation): boolean {
		const locationBeforeAfter = location.after ?? location.before;

		if (!locationBeforeAfter) {
			throw new Error('Location data is missing');
		}

		return this.preview$.value.payload.schoolAdminsToLocations.some(link => {
			const beforeAfter = link.after ?? link.before;

			if (!beforeAfter) {
				throw new Error('SchoolAdminToLocation link has no before or after state');
			}

			const doesLocationMatch =
				(TextUtils.isNonEmptyString(beforeAfter.location_uuid) &&
					beforeAfter.location_uuid === locationBeforeAfter.location_uuid) ||
				(TextUtils.isNonEmptyString(beforeAfter.sso_location_id) &&
					beforeAfter.sso_location_id === locationBeforeAfter.sso_location_id);

			if (!doesLocationMatch) {
				return false;
			}

			if (!link.before && link.after) {
				// added
				return true;
			} else if (link.before && !link.after) {
				// existing
				return false;
			} else if (link.before && link.after && link.before.active !== link.after.active) {
				// assignment status changed
				return true;
			}

			return false;
		});
	}
}
